import { useCallback } from 'react';

import { type UseMutationOptions, useMutation } from '@tanstack/react-query';

/**
 * useMutation for calls that send or return a secret value. React Query keeps
 * a mutation's variables and data in the MutationCache; this hook resets the
 * mutation once it settles and sets gcTime to 0, so the cache drops it at
 * once. Only mutateAsync and isPending are exposed: the caller keeps the
 * returned value in its own state and clears it when done.
 */
export function useSecretMutation<TData, TVariables = void>(
  options: Omit<UseMutationOptions<TData, Error, TVariables>, 'gcTime'>
) {
  const { mutateAsync, reset, isPending } = useMutation({
    ...options,
    gcTime: 0,
  });

  const run = useCallback(
    async (variables: TVariables) => {
      try {
        return await mutateAsync(variables);
      } finally {
        reset();
      }
    },
    [mutateAsync, reset]
  );

  return { mutateAsync: run, isPending };
}
